import { useState } from 'react';
import { Ban } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { useBlockMember } from '@/pages/team/hooks/useTeamQueries';
import type { UserMember } from '@/pages/team/types';

interface BlockMemberDialogProps {
  member: UserMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Подтверждение блокировки участника — amber (пауза, обратимо в БД).
 * Действие = DELETE /users/{member_id} (is_active=false). Данные и история
 * сохраняются.
 */
export function BlockMemberDialog({ member, open, onOpenChange }: BlockMemberDialogProps) {
  const block = useBlockMember();
  const [error, setError] = useState<string | undefined>();

  if (!member) return null;

  const displayName =
    member.display_name ||
    [member.first_name, member.last_name].filter(Boolean).join(' ') ||
    member.email;

  const submit = async () => {
    setError(undefined);
    try {
      await block.mutateAsync(member.member_id);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Не удалось заблокировать');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-accent-brand/15 text-accent-brand">
            <Ban className="h-5 w-5" />
          </span>
          <DialogTitle>Заблокировать {displayName}?</DialogTitle>
          <DialogDescription>
            Участник потеряет доступ немедленно — активные сессии завершатся.
          </DialogDescription>
        </DialogHeader>

        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Данные и история его действий сохраняются</li>
          <li>Поля и осмотры за ним остаются</li>
          <li>Доступ можно вернуть в любой момент — «Разблокировать»</li>
        </ul>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={block.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={block.isPending}
          >
            {block.isPending ? 'Блокируем…' : 'Заблокировать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
